import { useTranslation } from "react-i18next";
import { SignatureConfig, Workflow } from "../interfaces";
import { ChevronRightIcon, UserCircleIcon } from "@heroicons/react/outline";
import SmallTextInputField from "./SmallTextInputField";
import SmallTextArea from "./SmallTextArea";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import useUser from "../hooks/useUser";
import SmallSelectField from "./SmallSelectField";
import useWorkflow from "../hooks/useWorkflow";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { putData } from "../backend";

interface Props {
  signatureConfig: SignatureConfig;
  updateSignatureConfig: (updates: Partial<SignatureConfig>) => void;
  handleSaveAndNext: () => void;
  handleBack: () => void;
  isSending: boolean;
}

function SignaturesSidebarConfirmation(props: Props) {
  const { t } = useTranslation();
  const { signatureConfig } = props;

  const { user, hasPermission } = useUser();

  const { id } = useParams<{ id: string | undefined }>();
  const { workflow: initialWorkflow, mutateWorkflow } = useWorkflow(id!);

  const [workflow, setWorkflow] = useState<Workflow>();

  useEffect(() => {
    if (initialWorkflow?.id !== workflow?.id)
      setWorkflow(initialWorkflow);
  }, [initialWorkflow, workflow]);

  function updateWorkflowLanguage(language: string) {
    const updatedWorkflow = { ...workflow!, language };
    setWorkflow(updatedWorkflow);

    const returnedWorkflow = putData(`/workflows/${workflow!.id}/`, updatedWorkflow);
    mutateWorkflow(returnedWorkflow);
  }

  return (

    <div className="flex flex-col w-[360px] h-full bg-white flex-shrink-0 pt-6 px-4 border-l">
      <div className="text-2xl font-semibold font-gilroy mb-2">
        Confirmation
      </div>
      <div className="text-sm font-semibold text-gray-400 mb-2">
        {t("Please review your document and make sure it's ready for sending.")}
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0" />
      <div className="font-semibold font-gilroy my-2">
        Recipients
      </div>

      <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hidden pb-4">
        {signatureConfig.numRecipients >= 1 &&
          <div className="flex flex-col border border-blue rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-blue bg-blue/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                1st Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName1} {"<"}{signatureConfig.recipientEmail1}{">"}
              </div>
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 2 &&
          <div className="flex flex-col border border-purple rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-purple bg-purple/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                2nd Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName2} {"<"}{signatureConfig.recipientEmail2}{">"}
              </div>
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 3 &&
          <div className="flex flex-col border border-green rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-green bg-green/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                3rd Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName3} {"<"}{signatureConfig.recipientEmail3}{">"}
              </div>
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 4 &&
          <div className="flex flex-col border border-red rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-red bg-red/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                4th Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName4} {"<"}{signatureConfig.recipientEmail4}{">"}
              </div>
            </div>
          </div>
        }
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0" />

      <div className="font-semibold font-gilroy my-2">
        Email Message
      </div>

      <div className="flex flex-col gap-2">
        <SmallTextInputField label="Subject" value={signatureConfig.emailSubject} onChange={(value) => props.updateSignatureConfig({ "emailSubject": value })} />
        <SmallTextArea label="Content" value={signatureConfig.emailContent} onChange={(value) => props.updateSignatureConfig({ "emailContent": value })} />
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-4" />

      <div className="font-semibold font-gilroy my-2">
        PDF Settings
      </div>

      <div className="flex justify-start items-center font-gilroy font-semibold text-sm  w-full">
        <ToggleButton value={signatureConfig.enableAuditTrail} onChange={value => props.updateSignatureConfig({ "enableAuditTrail": value })} />
        <div className={`font-semibold text-sm ml-2 mr-1 text-gray-600`}>
          {t("Add audit trail to completed PDF")}
        </div>
        <ProLabel />
      </div>

      {!!workflow &&
        <SmallSelectField className="w-72 mt-4" label={t("Language")} value={workflow.language || workflow.owner!.language} onChange={updateWorkflowLanguage}>
          <option value={"en"}>{t("English")}</option>
          <option value={"es"}>{t("Spanish")}</option>
          <option value={"fr"}>{t("French")}</option>
          <option value={"de"}>{t("German")}</option>
        </SmallSelectField>
      }

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
      {props.isSending &&
        <div className="flex items-center text-sm gap-2 mt-4 mb-4">
          <button className="btn btn-blue w-full px-0 py-2 flex items-center" disabled>Sending...</button>
        </div>
      }
      {!props.isSending &&
        <div className="flex items-center text-sm gap-2 mt-4 mb-4">
          <button className="btn btn-gray border w-full px-0 py-2" onClick={props.handleBack}>Back</button>
          <button className="btn btn-blue w-full px-0 py-2 flex items-center" disabled={!hasPermission(user?.activeTeam, "workflows.run")} onClick={props.handleSaveAndNext}>Send <ChevronRightIcon className="w-5 h-5 ml-1 mb-0.5 -mt-0.5" /></button>
        </div>
      }
    </div>
  );

}

export default SignaturesSidebarConfirmation;
